import { ReactNode } from "react"
import styled from "styled-components"

import { UITypes } from "types"

import { getPercentage } from "utils/getPercentage"
import WidgetsLayout from "layouts/WidgetsLayout"
import { ChartEmptyState } from "components/chart/ChartEmptyState"
import { GaugeSkeleton } from "components/chart/ChartEmptyState/skeletons"
import { type ChartOptions, RainbowChart } from "components/common/RainbowChart"
import { Spinner } from "components/common/Spinner"

import { HalfDoughnutSummaryItem, WidgetValueWrapper } from "./legend"

export { HalfDoughnutLegendItem, HalfDoughnutLegendGrid, HalfDoughnutLegendGroups } from "./legend"
export type { HalfDoughnutSummaryItem } from "./legend"

const StyledWidgetLayout = styled(WidgetsLayout)`
  max-width: initial;
  flex: 1 0 300px;
`

const WidgetContent = styled.div<{ $disabled?: boolean }>`
  position: relative;
  margin-top: 20px !important;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  cursor: ${({ $disabled }) => ($disabled ? "default" : "auto")};
  min-height: 200px;
`

const ChartWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50px;
  position: relative;
`

const Chart = styled.div`
  position: absolute;
  bottom: -126px;
`

const Text = styled.span`
  font-size: 12px;
  color: #64748b;
  max-width: 120px;
  text-align: center;
  text-transform: uppercase;
`

const WidgetMainValue = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 16px 0 10px;
`

const ValueWithPercentage = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 8px;

  .percentage {
    font-size: 18px;
    font-weight: 300;
    color: #475569;
  }
`

const WidgetFooter = styled.footer<{ $wrap?: boolean; $centeredWrap?: boolean }>`
  border-top: 1px solid #f1f5f9;
  padding-top: 16px;
  display: flex;
  flex-wrap: ${({ $wrap, $centeredWrap }) => ($wrap || $centeredWrap ? "wrap" : "nowrap")};
  justify-content: center;
  gap: ${({ $wrap, $centeredWrap }) => ($centeredWrap ? "4px 24px" : $wrap ? "8px 16px" : "0")};
`

const WidgetFooterItem = styled.div<{
  $wrap?: boolean
  $columns?: number
  $inline?: boolean
  $centeredWrap?: boolean
}>`
  text-align: ${({ $centeredWrap }) => ($centeredWrap ? "left" : "center")};
  border-right: ${({ $wrap, $centeredWrap }) => ($wrap || $centeredWrap ? "none" : "1px solid #f1f5f9")};
  flex: ${({ $wrap, $columns, $centeredWrap }) =>
    $centeredWrap ? "0 0 auto" : $wrap ? `1 1 calc(${100 / ($columns || 3)}% - 12px)` : "1"};
  min-width: ${({ $wrap, $centeredWrap }) => ($wrap && !$centeredWrap ? "80px" : $centeredWrap ? "0" : "0")};
  display: flex;
  flex-direction: ${({ $inline, $centeredWrap }) => ($inline || $centeredWrap ? "row" : "column")};
  align-items: center;
  justify-content: ${({ $centeredWrap }) => ($centeredWrap ? "flex-start" : "center")};
  gap: ${({ $inline, $centeredWrap }) => ($inline || $centeredWrap ? "4px" : "0")};

  &:last-child {
    border: none;
  }
`

const WidgetNote = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  color: #64748b;

  &::before {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    display: inline-flex;
    margin-right: 4px;
    content: "";
    background-color: ${({ $color }) => $color};
  }
`

const WidgetLoader = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`

interface HalfDoughnutSummaryWidgetProps {
  title: string
  mainLabel: string
  items: HalfDoughnutSummaryItem[]
  mainValue?: number
  chartOptions?: ChartOptions
  isFetching?: boolean
  showPercentage?: boolean
  legendWrap?: boolean
  legendInline?: boolean
  legendCenteredWrap?: boolean
  legendContent?: ReactNode
  dateSelectOptions?: UITypes.Option[]
  defaultSelectedDateOption?: UITypes.Option
  onSelectDateRange?: (date: [string, string]) => void
  minDate?: string
}

export const HalfDoughnutSummaryWidget = ({
  title,
  mainLabel,
  items,
  mainValue,
  chartOptions,
  isFetching = false,
  showPercentage = false,
  legendWrap = false,
  legendInline = false,
  legendCenteredWrap = false,
  legendContent,
  dateSelectOptions,
  defaultSelectedDateOption,
  onSelectDateRange,
  minDate,
}: HalfDoughnutSummaryWidgetProps) => {
  const total = items.reduce((sum, item) => sum + item.value, 0)
  const colors = items.map(item => item.color)
  const values = items.map(item => item.value)
  const legendColumns = legendWrap && legendInline && !legendCenteredWrap ? 4 : 3
  const useInlineStyle = legendInline || legendCenteredWrap
  const showEmptyState = total === 0 && !isFetching

  const renderItem = (item: HalfDoughnutSummaryItem) => (
    <WidgetFooterItem
      key={item.label}
      $wrap={legendWrap}
      $columns={legendColumns}
      $inline={useInlineStyle}
      $centeredWrap={legendCenteredWrap}
    >
      <WidgetNote $color={item.color}>{item.label}</WidgetNote>
      <WidgetValueWrapper $inline={useInlineStyle}>
        {showPercentage ? (
          <ValueWithPercentage>
            {item.value.toLocaleString()}
            <span className="percentage">{getPercentage(item.value, total)}</span>
          </ValueWithPercentage>
        ) : (
          item.value.toLocaleString()
        )}
      </WidgetValueWrapper>
    </WidgetFooterItem>
  )

  return (
    <StyledWidgetLayout
      title={title}
      minDate={minDate}
      onSelectDateRange={onSelectDateRange}
      dateSelectOptions={dateSelectOptions}
      defaultSelectedDateOption={defaultSelectedDateOption}
    >
      <WidgetContent $disabled={isFetching}>
        <ChartWrapper>
          <Chart>
            {showEmptyState ? (
              <ChartEmptyState skeleton={<GaugeSkeleton options={chartOptions} />} />
            ) : (
              <RainbowChart colors={colors} values={values} options={chartOptions} />
            )}
          </Chart>
        </ChartWrapper>
        {!showEmptyState && !isFetching && (
          <>
            <WidgetMainValue>
              <Text>{mainLabel}</Text>
              <WidgetValueWrapper>{(mainValue ?? total).toLocaleString()}</WidgetValueWrapper>
            </WidgetMainValue>
            <WidgetFooter $wrap={legendWrap} $centeredWrap={legendCenteredWrap}>
              {legendContent ?? items.map(renderItem)}
            </WidgetFooter>
          </>
        )}
        {isFetching && (
          <WidgetLoader>
            <Spinner />
          </WidgetLoader>
        )}
      </WidgetContent>
    </StyledWidgetLayout>
  )
}
